import { useState } from "react";
import { repoErrorMessage } from "@/components/repo-lifecycle/lifecycleVocab";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useScaffoldPlugin } from "@/api/mutations/repoScaffold";

// #276 (UC-2): „Plugin anlegen" — erzeugt die Plugin-Struktur und trägt sie in den Katalog ein.
//
// ⚠ Die Maske endet mit einem ENTWURF, nicht mit „ist live". Der Katalog ist ein bestehendes
// Repo; jede Änderung daran läuft über Entwurf + Freigabe (sonst wäre die Maske der Bypass um
// die Freigabe). Genau das muss die Rückmeldung sagen — ein „angelegt ✓" wäre gelogen.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marketplaceId: string;
  marketplaceName?: string;
  onCreated?: (draftBranch: string, pluginName: string) => void;
}

const EMPTY = { name: "", description: "", placement: "in-marketplace" as const, repoName: "" };

export function CreatePluginDialog({ open, onOpenChange, marketplaceId, marketplaceName, onCreated }: Props) {
  const [form, setForm] = useState<{
    name: string;
    description: string;
    placement: "in-marketplace" | "own-repo";
    repoName: string;
  }>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const mutation = useScaffoldPlugin();
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  function close(o: boolean) {
    if (!o) {
      setForm({ ...EMPTY });
      setError(null);
    }
    onOpenChange(o);
  }

  async function submit() {
    setError(null);
    const name = form.name.trim();
    if (!name) {
      setError("Ein Name ist erforderlich.");
      return;
    }
    try {
      const res = await mutation.mutateAsync({
        marketplaceId,
        name,
        description: form.description.trim() || undefined,
        placement: form.placement,
        repoName: form.placement === "own-repo" ? form.repoName.trim() || undefined : undefined,
      });
      toast.success(`Plugin „${name}" liegt zur Freigabe bereit`, {
        // #293 (UC-5): kein Branch-Name im Fach-Text — der Speicherort ist Infrastruktur.
        description: "Erst nach der Freigabe im Katalog.",
      });
      onCreated?.(res.draftBranch, name);
      close(false);
    } catch (err) {
      // EINE Fehlercode-Tabelle für alle Repo-Dialoge (lifecycleVocab) — vorher hatte
      // jeder Dialog eine eigene Ternary-Kette mit teils anderen Sätzen für dieselben
      // Codes (Review-Fund #8: sechs driftende Tabellen).
      setError(repoErrorMessage(err, "Anlegen fehlgeschlagen"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="dialog-create-plugin">
        <DialogHeader>
          <DialogTitle>Plugin anlegen</DialogTitle>
          <DialogDescription>
            Erzeugt die Plugin-Struktur und trägt sie in den Katalog
            {marketplaceName ? ` „${marketplaceName}"` : ""} ein. Der Eintrag entsteht als
            <strong> Entwurf</strong> und wird erst mit der Freigabe wirksam.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="plg-name">Name</Label>
            <Input
              id="plg-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="mein-plugin"
              data-testid="input-plugin-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="plg-placement">Wo soll es liegen?</Label>
            <Select
              value={form.placement}
              onValueChange={(v) => set({ placement: v as "in-marketplace" | "own-repo" })}
            >
              <SelectTrigger id="plg-placement" data-testid="select-plugin-placement">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="in-marketplace">Im Katalog-Repository</SelectItem>
                <SelectItem value="own-repo">In einem eigenen Repository</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[12px] text-text3">
              {form.placement === "in-marketplace"
                ? "Einfachster Fall: das Plugin lebt im selben Repository wie der Katalog."
                : "Eigenes Repository — sinnvoll, wenn das Plugin eine eigene Historie oder andere Mitwirkende haben soll. Es erscheint danach als eigenes Repository in der Verwaltung."}
            </p>
          </div>

          {form.placement === "own-repo" && (
            <div className="space-y-1.5">
              <Label htmlFor="plg-repo">Repository-Name (optional)</Label>
              <Input
                id="plg-repo"
                value={form.repoName}
                onChange={(e) => set({ repoName: e.target.value })}
                placeholder={form.name.trim() ? `plugin-${form.name.trim()}` : "plugin-<name>"}
                data-testid="input-plugin-repo"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="plg-desc">Beschreibung (optional)</Label>
            <Input
              id="plg-desc"
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Was kann dieses Plugin?"
            />
          </div>

          {error && (
            <p className="text-[13px] text-danger" data-testid="create-plugin-error">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={mutation.isPending || !form.name.trim()}
            data-testid="submit-create-plugin"
          >
            {mutation.isPending ? "Lege an …" : "Als Entwurf anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
